import '../Public/public.css';
import './invoice.css';
import React from 'react';
import { isPaidStatus } from '../../utils/invoiceOverdue';   // Block H-5: refunds
import { invoiceAmounts } from '../../lib/invoiceAmounts';

// ─────────────────────────────────────────────────────────────────────────────
// THE ONE INVOICE RENDERER (item 34, Carla's approved launch set, 2026-10-20).
//
// Used by the public invoice page, the client-portal invoice modal and the owner's in-app view; the
// PDF (and Print, which opens the PDF) draws the same document on the server. It renders `doc`, the
// document lib/invoiceDocument built on the server, AS IT IS: every word, every rate label and every
// formatted figure comes from there, so the pages, the PDF and the charge cannot disagree. Nothing
// here computes a total or decides whether VAT is shown.
//
// A business that is VAT registered gets the full UK VAT invoice (supplier VAT number, tax point
// when different, unit price and amount excluding VAT, the rate per line, VAT per rate, total
// excluding VAT, total VAT, total payable); one that is not gets no VAT anywhere, because the
// document has none.
//
// `invoice` still carries what is not the document: the status pill, a refund, the notes.
// ─────────────────────────────────────────────────────────────────────────────

const CURRENCY_SYMBOLS = { GBP: '£', USD: '$', EUR: '€', CAD: 'CA$', AUD: 'A$', CHF: 'CHF', JPY: '¥', SEK: 'kr', NOK: 'kr', DKK: 'kr', NZD: 'NZ$', SGD: 'S$', AED: 'د.إ', ZAR: 'R' };
export function sym(c) { return CURRENCY_SYMBOLS[c] || c; }
// Block H: £1,200.00, with the thousands separator (it read £1200.00), as the portal list and the
// proposal already show money. Falls back to symbol + 2dp for a code Intl does not know.
export function fmtMoney(amount, currency) {
  const n = Number(amount || 0);
  try { return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'GBP' }).format(n); }
  catch { return `${sym(currency)}${n.toFixed(2)}`; }
}
export function fmtDate(str) {
  if (!str) return '—';
  // issueDate/dueDate are date-only ("2026-08-12"); paidAt from the payment webhook is a full
  // ISO timestamp ("2026-08-12T14:30:00.000Z"). Append noon ONLY to a bare date (so a date-only
  // value doesn't shift a day across timezones); never to a value that already carries a time,
  // which would make "…000ZT12:00:00" → Invalid Date.
  const s = String(str);
  const d = new Date(s.includes('T') ? s : s + 'T12:00:00');
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

// Item 34: the one rule (lib/invoiceAmounts, per line), kept under this name for its callers.
export function calcTotals(lineItems, currency = 'GBP') {
  const { subtotal, tax, total } = invoiceAmounts({ lineItems, currency });
  return { subtotal, tax, total };
}

// Status pill classes (invoice.css). Text is always ink; the tint carries the meaning.
export const STATUS_STYLES = {
  paid:      { tone: 'paid',    label: 'Paid' },
  pending:   { tone: 'due',     label: 'Due' },   // fresh-eyes 24: the same as sent
  overdue:   { tone: 'overdue', label: 'Overdue' },
  draft:     { tone: 'neutral', label: 'Draft' },
  sent:      { tone: 'due',     label: 'Due' },
  cancelled: { tone: 'neutral', label: 'Cancelled' },   // Block H: it showed "Status unavailable"
  // Block H-5 (Carla's decision 4): what the client sees after a refund.
  partially_refunded: { tone: 'neutral', label: 'Partially refunded' },
  refunded:  { tone: 'neutral', label: 'Refunded' },
};

// Deliberately outside STATUS_STYLES: it is not a status an invoice can have, it is
// what we show when we cannot read the one it has. Grey, so it carries no verdict.
const UNKNOWN_STATUS = { tone: 'neutral', label: 'Status unavailable' };

export default function InvoiceBody({ invoice = {}, doc, overdue = false, children }) {
  // NEVER ASSERT THE MOST CONSEQUENTIAL OF FIVE WHEN WE DO NOT KNOW (Carla, 2026-10-04): a status
  // this map does not recognise is shown as "Status unavailable", never as unpaid.
  const st = (overdue && !isPaidStatus(invoice.status) && invoice.status !== 'cancelled') ? STATUS_STYLES.overdue : (STATUS_STYLES[invoice.status] || UNKNOWN_STATUS);
  const refunded = isPaidStatus(invoice.status) ? Number(invoice.refundedAmount || 0) : 0;   // Block H-5

  // No document, no guessed invoice: an invoice rebuilt here from its raw lines is exactly the
  // second renderer item 34 removed. Said plainly instead.
  if (!doc) {
    return (
      <article className="pub-card inv">
        <div className="pub-card__pad inv-body">
          <p className="inv-cancelled" role="alert" data-testid="invoice-no-document">This invoice could not be shown. Please reload the page.</p>
          {children}
        </div>
      </article>
    );
  }

  const meta = (key) => doc.meta.find((m) => m.key === key);
  const number = meta('number');
  const po = meta('po');
  const dates = doc.meta.filter((m) => !['number', 'po'].includes(m.key));
  const vat = doc.showVat;
  const supplier = doc.supplier || {};
  const hasFrom = Boolean(supplier.name || (supplier.lines && supplier.lines.length) || supplier.vatNumber);

  return (
    <article className="pub-card inv" data-testid="invoice-document">
      <div className="pub-card__pad inv-body">
        <header className="inv-head">
          <div className="inv-head__id">
            <span className="pub-eyebrow">{doc.title || 'Invoice'}</span>
            <h1 className="pub-h1 inv-number">{number ? number.value : doc.number}</h1>
            {po && <span className="pub-sub">{po.label}: <strong>{po.value}</strong></span>}
          </div>
          <span className={`inv-status inv-status--${st.tone}`}>{st.label}</span>
        </header>
        <dl className="inv-dates">
          {dates.map((m) => (
            <div key={m.key} className={m.key === 'due' && st.tone === 'overdue' ? 'is-overdue' : m.key === 'paid' ? 'is-paid' : ''} data-testid={`invoice-meta-${m.key}`}>
              <dt>{m.label}</dt><dd>{m.value || '—'}</dd>
            </div>
          ))}
        </dl>

        {/* FROM / BILL TO, side by side: where a client looks on an invoice (re-test item 16:
            the VAT number with the company details at the top, not in the small print). */}
        <div className={`inv-parties${hasFrom ? ' inv-parties--two' : ''}`}>
          {hasFrom && (
            <div className="inv-party" data-testid="invoice-from">
              <span className="pub-eyebrow">From</span>
              {supplier.name && <span className="inv-party__name">{supplier.name}</span>}
              {(supplier.lines || []).map((ln, i) => <span key={i} className="pub-muted">{ln}</span>)}
              {/* The VAT number is what makes this a valid VAT invoice for the client's own records,
                  so it is the one line given weight. Present only when the business is registered. */}
              {supplier.vatNumber && <span className="inv-party__vat" data-testid="invoice-from-vat">VAT number {supplier.vatNumber}</span>}
            </div>
          )}
          <div className="inv-party" data-testid="invoice-bill-to">
            <span className="pub-eyebrow">Bill to</span>
            <span className="inv-party__name">{doc.customer.name}</span>
            {doc.customer.company && <span>{doc.customer.company}</span>}
            {doc.customer.addressLines.map((ln, i) => <span key={i} className="pub-muted">{ln}</span>)}
            {doc.customer.email && <span className="pub-muted">{doc.customer.email}</span>}
          </div>
        </div>

        <div className={`pub-lines${vat ? ' inv-lines--vat' : ''}`} role="table" aria-label="Items">
          <div className="pub-lines__head" role="row">
            {doc.columns.map((c) => <span key={c} role="columnheader">{c}</span>)}
          </div>
          {doc.lines.map((li, idx) => (
            <div key={idx} className="pub-lines__row" role="row" data-testid="invoice-line">
              <span className="pub-lines__name" role="cell">
                {li.description}
                {li.note && <span className="inv-tax inv-line-note"> {li.note}</span>}
              </span>
              <span className="pub-lines__qty" role="cell"><span className="pub-lines__lab">Qty </span>{li.qty}</span>
              <span className="pub-lines__price" role="cell"><span className="pub-lines__lab">at </span>{li.unitPrice}{vat && <span className="pub-lines__lab"> excl. VAT</span>}</span>
              {vat && <span className="pub-lines__rate" role="cell">{/%$/.test(li.rate || '') && <span className="pub-lines__lab">VAT </span>}{li.rate}</span>}
              <span className="pub-lines__total" role="cell">{li.amount}</span>
            </div>
          ))}
        </div>

        {doc.vatByRate.length > 0 && (
          <div className="inv-vatrates" role="table" aria-label="VAT by rate" data-testid="invoice-vat-by-rate">
            <div className="inv-vatrates__row inv-vatrates__head" role="row">
              <span role="columnheader">VAT summary</span><span role="columnheader">Net</span><span role="columnheader">VAT</span>
            </div>
            {doc.vatByRate.map((r) => (
              <div key={r.label} className="inv-vatrates__row" role="row">
                <span role="cell">{r.label}</span><span role="cell">{r.net}</span><span role="cell">{r.vat}</span>
              </div>
            ))}
          </div>
        )}

        <dl className="pub-totals" data-testid="invoice-totals">
          {doc.totals.map((t) => (
            <div key={t.key} className={t.grand ? 'pub-totals__grand' : ''}>
              <dt>{t.grand ? `${t.label} (${doc.currency})` : t.label}</dt><dd>{t.value}</dd>
            </div>
          ))}
          {refunded > 0 && <div className="inv-refunded" data-testid="invoice-refunded"><dt>Refunded</dt><dd>−{fmtMoney(refunded, doc.currency)}</dd></div>}
        </dl>

        {invoice.notes && (
          <div className="inv-notes">
            <span className="pub-eyebrow">Notes</span>
            <p className="inv-pre">{invoice.notes}</p>
          </div>
        )}

        {children}
      </div>
    </article>
  );
}
